import React from 'react';
import { Download } from 'lucide-react';
import BulletedItem from './ui/BulletedItem';

const TONE_GRADIENT = {
  blue: 'from-slate-50 via-white to-white',
  red: 'from-slate-50 via-white to-white'
};

const HOVER_BORDER = {
  blue: 'border-transparent hover:border-indigo-300/60',
  red: 'border-transparent hover:border-indigo-300/60'
};

const TIMELINE_DOT = {
  blue: 'bg-indigo-600',
  red: 'bg-indigo-600'
};

export default function ProfileCard({ perfil, tone = 'blue', accent, border, badge }) {
  if (!perfil) {
    return null;
  }

  const biografia = Array.isArray(perfil.biografia) ? perfil.biografia : [];
  const trayectoria = Array.isArray(perfil.trayectoria) ? perfil.trayectoria : [];
  const valores = Array.isArray(perfil.valores) ? perfil.valores : [];
  const gradient = TONE_GRADIENT[tone] ?? TONE_GRADIENT.blue;
  const timelineDot = TIMELINE_DOT[tone] ?? TIMELINE_DOT.blue;

  return (
    <article
      className={`border ${border} rounded-3xl shadow-sm overflow-hidden bg-gradient-to-br ${gradient}`}
    >
      <div className="relative h-64 sm:h-80 overflow-hidden">
        <img src={perfil.foto} alt={perfil.nombreCompleto} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/15 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 text-white">
          <p className="text-[10px] font-bold uppercase tracking-[0.35em] opacity-80">Perfil Presidencial</p>
          <h3 className="mt-2 text-2xl sm:text-3xl font-extrabold leading-tight">{perfil.nombreCompleto}</h3>
        </div>
      </div>

      <div className="p-8 pt-7">
        <div className="flex items-start justify-between gap-4">
          <div>
            <span
              className={`inline-flex text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-widest ${badge}`}
            >
              {perfil.ideologia}
            </span>
            <p className="mt-3 text-sm font-medium text-slate-500">
              {perfil.edad} años · {perfil.profesion}
            </p>
          </div>
        </div>

        <blockquote className={`mt-6 text-2xl font-extrabold leading-tight ${accent}`}>
          &ldquo;{perfil.lema}&rdquo;
        </blockquote>

        <div className="mt-6 space-y-4 text-slate-600 leading-8">
          {biografia.map((parrafo, index) => (
            <p key={`${perfil.nombreCompleto}-bio-${index}`}>{parrafo}</p>
          ))}
        </div>

        <a
          href={perfil.planGobiernoUrl ?? '#'}
          download
          className={`mt-8 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 px-5 py-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-slate-700 border ${HOVER_BORDER[tone] ?? HOVER_BORDER.blue}`}
        >
          <Download className="h-4 w-4" />
          Descargar Plan de Gobierno Completo (PDF)
        </a>

        <div className="mt-8">
          <h4 className="text-sm font-bold uppercase tracking-widest text-slate-400 mb-4">Trayectoria</h4>
          <div className="space-y-4">
            {trayectoria.map((hito, index) => (
              <BulletedItem
                key={`${perfil.nombreCompleto}-tray-${index}`}
                as="div"
                className="gap-4"
                marker={<div className={`w-3 h-3 rounded-full ${timelineDot}`} />}
                markerClassName="mt-2"
              >
                <p className="text-sm text-slate-600 leading-7">{hito}</p>
              </BulletedItem>
            ))}
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          {valores.map((valor) => (
            <span
              key={`${perfil.nombreCompleto}-valor-${valor}`}
              className="px-3 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold"
            >
              {valor}
            </span>
          ))}
        </div>
      </div>
    </article>
  );
}
